import { logger } from '../lib/logger';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ClipboardList, Search, Download, Trash2, Menu, LogOut, Home, Users, ClipboardCheck, BarChart3, Newspaper, Bot, Mail, Calendar } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import AdminNav from '../components/AdminNav';

const AdminSurveys = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [respostas, setRespostas] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterModelo, setFilterModelo] = useState('all');
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const verifyAuth = async () => {
      try {
        await axios.get(`${BACKEND_URL}/api/admin/verify`, { withCredentials: true });
        localStorage.removeItem('admin_token');
        fetchData();
      } catch (error) {
        logger.error('Sessão inválida:', error);
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
    };
    verifyAuth();
    // eslint-disable-next-line
  }, [navigate]);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/downloads/surveys?limit=2000`, { withCredentials: true });
      setRespostas(res.data.respostas || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      logger.error('Erro ao buscar respostas:', error);
      toast({ title: 'Erro ao carregar', description: 'Não foi possível carregar as respostas.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deletar esta resposta?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/downloads/surveys/${id}`, { withCredentials: true });
      toast({ title: 'Resposta deletada' });
      fetchData();
    } catch (error) {
      toast({ title: 'Erro ao deletar', variant: 'destructive' });
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/logout`, {}, { withCredentials: true });
    } catch (e) { /* ignore */ }
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  const modelos = [...new Set(respostas.map(r => r.modelo).filter(Boolean))].sort();

  const hojeStr = new Date().toDateString();
  const capturadosHoje = respostas.filter(r => new Date(r.created_at).toDateString() === hojeStr).length;
  const inicio7d = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const capturados7d = respostas.filter(r => new Date(r.created_at).getTime() >= inicio7d).length;

  const filtered = respostas.filter((r) => {
    if (filterModelo !== 'all' && r.modelo !== filterModelo) return false;
    if (searchTerm) {
      const t = searchTerm.toLowerCase();
      return (r.nome || '').toLowerCase().includes(t) || (r.email || '').toLowerCase().includes(t) || (r.onde_comprou || '').toLowerCase().includes(t);
    }
    return true;
  });

  const exportToCSV = () => {
    const csvContent = [
      ['Nome', 'Email', 'Modelo', 'Nascimento', 'Onde comprou', 'Sugestão', 'Data'],
      ...filtered.map(r => [
        r.nome || '',
        r.email || '',
        r.modelo || '',
        r.data_nascimento || '',
        (r.onde_comprou || '').replace(/[\n,]/g, ' '),
        (r.sugestao || '').replace(/[\n,]/g, ' '),
        new Date(r.created_at).toLocaleDateString('pt-BR'),
      ])
    ].map(row => row.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `pesquisa_downloads_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast({ title: 'Exportação concluída', description: `${filtered.length} respostas exportadas.` });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-xl">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex">
      <div className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-gray-900 border-r border-gray-800 transition-all duration-300`}>
        <div className="p-4">
          <div className="flex items-center justify-between mb-8">
            {sidebarOpen && <h1 className="text-xl font-bold text-yellow-400">WING ADMIN</h1>}
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-gray-400 hover:text-white p-2">
              <Menu className="w-6 h-6" />
            </button>
          </div>
          <AdminNav active="pesquisa" navigate={navigate} sidebarOpen={sidebarOpen} onLogout={handleLogout} />
        </div>
      </div>

      <div className="flex-1 p-8 overflow-auto">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">Pesquisa da Central de Downloads</h1>
            <p className="text-gray-400">{filtered.length} de {total} respostas</p>
          </div>

          {/* Contador de leads captados */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            <div className="glass-dark rounded-xl border border-yellow-500/20 p-5" data-testid="stat-leads-total">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 bg-yellow-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Users className="w-6 h-6 text-yellow-400" />
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Leads captados (total)</p>
                  <p className="text-2xl font-bold text-white">{total}</p>
                </div>
              </div>
            </div>
            <div className="glass-dark rounded-xl border border-white/10 p-5" data-testid="stat-leads-hoje">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Captados hoje</p>
                  <p className="text-2xl font-bold text-white">{capturadosHoje}</p>
                </div>
              </div>
            </div>
            <div className="glass-dark rounded-xl border border-white/10 p-5" data-testid="stat-leads-7d">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <BarChart3 className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Últimos 7 dias</p>
                  <p className="text-2xl font-bold text-white">{capturados7d}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="glass-dark p-4 rounded-xl border border-white/10 mb-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar por nome, e-mail ou loja..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500"
                  data-testid="survey-search-input"
                />
              </div>
              <select
                value={filterModelo}
                onChange={(e) => setFilterModelo(e.target.value)}
                className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-yellow-500"
                data-testid="survey-filter-modelo"
              >
                <option value="all" className="bg-gray-900">Todos os modelos</option>
                {modelos.map(m => (
                  <option key={m} value={m} className="bg-gray-900">{m}</option>
                ))}
              </select>
              <button
                onClick={exportToCSV}
                className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                data-testid="survey-export-btn"
              >
                <Download className="w-5 h-5" />
                Exportar CSV
              </button>
            </div>
          </div>

          <div className="glass-dark rounded-xl border border-white/10 overflow-hidden">
            {filtered.length === 0 ? (
              <p className="text-gray-400 text-center py-12" data-testid="survey-empty">Nenhuma resposta encontrada.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-800 bg-white/5">
                      <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">Nome</th>
                      <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">Contato</th>
                      <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">Modelo</th>
                      <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">Onde comprou</th>
                      <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">Sugestão</th>
                      <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">Data</th>
                      <th className="text-center py-4 px-4 text-gray-400 font-medium text-sm">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((r) => (
                      <tr key={r.id} className="border-b border-gray-800 hover:bg-white/5 transition-colors" data-testid={`survey-row-${r.id}`}>
                        <td className="py-4 px-4 text-white font-medium">{r.nome}</td>
                        <td className="py-4 px-4">
                          <div className="text-gray-300 text-sm flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{r.email}</div>
                          {r.data_nascimento && <div className="text-gray-500 text-xs flex items-center gap-1 mt-1"><Calendar className="w-3.5 h-3.5" />{r.data_nascimento}</div>}
                        </td>
                        <td className="py-4 px-4">
                          <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded text-xs font-medium">{r.modelo}</span>
                        </td>
                        <td className="py-4 px-4 text-gray-300 text-sm max-w-[180px]">{r.onde_comprou || '-'}</td>
                        <td className="py-4 px-4 text-gray-300 text-sm max-w-[240px]">{r.sugestao || '-'}</td>
                        <td className="py-4 px-4 text-gray-400 text-sm">{new Date(r.created_at).toLocaleDateString('pt-BR')}</td>
                        <td className="py-4 px-4">
                          <div className="flex items-center justify-center">
                            <button onClick={() => handleDelete(r.id)} className="p-2 text-red-400 hover:bg-red-500/10 rounded transition-colors" title="Deletar" data-testid={`survey-delete-${r.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSurveys;
